import React, { forwardRef, useEffect, useState, useRef, useMemo, useId } from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import QuestionService from '@/api/question'
import currencies from '@/util/currencies'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useSelector } from 'react-redux'
import { selectUserOrders } from '@/store/wagers'
import { useActiveCurrency, useCurrentUser } from '@/hooks'
import { selectPageLayout } from '@/store/app'
import constants from '@/constants'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import BinaryButtons from '@/components/ui/binary-buttons'
import Button from '@/components/ui/button/Button'
import Thumbnail from '@/components/ui/thumbnail'
import CustomBinaryMarketBetButton from '@/components/ui/custom-binary-market-bet-button/CustomBinaryMarketBetButton'
import { i18n } from '@/locale/i18n'

import './OutcomeItem.scss'
import DeferredNumberFlow from '@/components/ui/deferred-number-flow'
import usePageSettled from '@/hooks/usePageSettled'
import ArrowCaretDownIcon from '@/assets/icons/ArrowCaretDownIcon.jsx'
import ArrowCaretUpIcon from '@/assets/icons/ArrowCaretUpIcon.jsx'
import Tag from '@/components/ui/status-tag'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi } from '@/features/market/utils/marketType'
import OutcomeAccordion from './OutcomeAccordion'
import { useOutcomeOrderBook } from './hooks/useOutcomeOrderBook'
import { isClickable, isWinnerState, isWrongPredictionState, isPredictionState, isDisabledState } from './utils/outcomeState'
import { getOrderStatus } from './utils/orderStatus.jsx'

const OutcomeItem = (
  {
    big,
    outcome,
    selected,
    onClick,
    question,
    parentQuestion,
    currencyId = currencies.OOM.id,
    noAnimation,
    hideOutcomePrice,
    prediction,
    inMarketPage,
    className,
    // Accordion props
    isAccordionOpen = false,
    onAccordionToggle,
    displayMode = 'segment' /* can be "segment" or "binary" */,
    onPriceClick = () => {},
    // Global outcome selection from parent (for syncing OrderBook tabs)
    currentOutcomeSelection = null,
    // Global position from parent (for syncing OrderBook tabs in multi-outcome markets)
    globalPosition = null,
    // New props for market type refactor
    marketType = null,
    shouldShowPositionButtons = false,
    outcomeLength = 2,
  },
  ref
) => {
  const isMobileSize = useIsMobileSize() 
  const isOrderBookAvailable = (question || parentQuestion)?.order_book_enabled
  const isAccordion = inMarketPage && (isOrderBookAvailable || outcome?.description || outcome?.resolution_description)
  /** Expanded, or picked in PlaceBet — both mean "this is the market being acted on". */
  const isActiveMarket = isAccordionOpen || selected === true
  const activeCurrency = useActiveCurrency()
  const currentUser = useCurrentUser()
  const { adminMode } = useSelector(selectPageLayout)
  const [position, setPosition] = useState(constants.position.LONG)
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  useEffect(() => {
    const marketQuestion = question || parentQuestion
    if (!marketQuestion || !outcome) return

    // For virtual outcomes (mono markets), use the virtual position
    if (outcome._virtualPosition !== undefined) {
      setPosition(outcome._virtualPosition)
      return
    }

    const ui = deriveMarketUi(marketQuestion)
    const isYesNoMarket = Boolean(ui.binaryLabelStyle) && (marketQuestion?.outcomes?.length === 2)

    if (isYesNoMarket && currentOutcomeSelection && marketQuestion.outcomes && marketQuestion.outcomes.length >= 2) {
      if (currentOutcomeSelection.id === marketQuestion.outcomes[0].id) {
        setPosition(constants.position.LONG)
      } else if (currentOutcomeSelection.id === marketQuestion.outcomes[1].id) {
        setPosition(constants.position.SHORT)
      }
    } else if (globalPosition !== null && globalPosition !== undefined) {
      setPosition(globalPosition)
    }
  }, [
    currentOutcomeSelection?.id,
    globalPosition,
    outcome?.id,
    outcome?.title,
    outcome?._virtualPosition,
    question?.outcomes,
    parentQuestion?.outcomes,
    question?.outcomes?.[0]?.position_labels,
    parentQuestion?.outcomes?.[0]?.position_labels,
    question?.outcomes?.[1]?.position_labels,
    parentQuestion?.outcomes?.[1]?.position_labels,
    isAccordionOpen,
    position,
  ])

  const isMultiOutcomeMarket = ((question || parentQuestion)?.outcomes?.length ?? 0) > 1
  const userOrders = useSelector(selectUserOrders()) || []

  // Use custom hook for orderbook management
  const { orderbook, longOrderbook, shortOrderbook } = useOutcomeOrderBook({
    outcome,
    question,
    parentQuestion,
    activeCurrency,
    isAccordion,
    isMultiOutcomeMarket,
    isAccordionOpen,
    position,
    setPosition,
    setActiveTabIndex,
  })

  // Orderbook crossed detection (staff mode only)
  const isOrderBookCrossed = useMemo(() => {
    if (!adminMode || !currentUser?.is_staff) return false
    if (!orderbook?.buySeries || !orderbook?.sellSeries) return false

    const buySeries = orderbook.buySeries
    const sellSeries = orderbook.sellSeries

    for (let i = 1; i < buySeries.length; i++) {
      if (buySeries[i].price > buySeries[i - 1].price) {
        return true
      }
    }

    for (let i = 1; i < sellSeries.length; i++) {
      if (sellSeries[i].price > sellSeries[i - 1].price) {
        return true
      }
    }

    return false
  }, [orderbook?.buySeries, orderbook?.sellSeries, adminMode, currentUser?.is_staff])

  // Use utility functions for state checking
  const isDisabled = !question && !parentQuestion ? false : isDisabledState(outcome, prediction, question, parentQuestion)
  const isWinner = !question && !parentQuestion ? false : isWinnerState(outcome, question, parentQuestion)
  const clickable = !question && !parentQuestion ? false : isClickable(outcome, question, parentQuestion, currencyId)
  const isPrediction = !question && !parentQuestion ? false : isPredictionState(outcome, prediction, question, parentQuestion)
  const isPredictionWrong = !question && !parentQuestion ? false : isWrongPredictionState(outcome, prediction, question, parentQuestion)
  const isCanceled = !question && !parentQuestion ? false : outcome.disabled
  const isQuestionResolved =
    !question && !parentQuestion
      ? false
      : QuestionService.isResolved(question || parentQuestion) ||
        (question || parentQuestion)?.status === QUESTION_STATUS.RESOLVED
  const isCustomBinary = marketType === 'independent_mono_custom'
  const marketCurrencyId =
    !question && !parentQuestion ? null : currencyId === 'OOM' ? 'OOM' : question.canonical_currency
  // Skip the 1% -> target% entry animation when the page hasn't settled yet
  // (initial load / LCP). After the page is settled, future mounts get the
  // animation back. Live price changes always animate via the CSS transition
  // on `.outcome-item__chart`.
  const pageSettledAtMount = usePageSettled()
  const [width, setWidth] = useState(
    noAnimation || !pageSettledAtMount ? outcome.price[marketCurrencyId] * 100 : 1
  )
  const [isHovered, setIsHovered] = useState(false)
  const accordionId = useId()
  const accordionPanelId = `${accordionId}-panel`
  const accordionTriggerId = `${accordionId}-trigger`
  const [value, setValue] = useState(outcome.price[marketCurrencyId])
  const [colorClass, setColorClass] = useState('')
  const prevValueRef = useRef(outcome.price[marketCurrencyId])

  useEffect(() => {
    if (!question && !parentQuestion) return
    const targetWidth = outcome.price[marketCurrencyId] * 100
    setWidth(targetWidth)

    const newValue = outcome.price[marketCurrencyId]
    setValue(newValue)

    if (prevValueRef.current !== newValue) {
      if (newValue > prevValueRef.current) {
        setColorClass('text-positive-4')
      } else if (newValue < prevValueRef.current) {
        setColorClass('text-negative-4')
      }

      const timer = setTimeout(() => {
        setColorClass('')
      }, 1000)

      prevValueRef.current = newValue

      return () => {
        clearTimeout(timer)
        setColorClass('')
      }
    }
  }, [outcome, marketCurrencyId, question, parentQuestion])

  const [yesButtonHovered, setYesButtonHovered] = useState(false)
  const [noButtonHovered, setNoButtonHovered] = useState(false)
  const [isBinaryAnimating, setIsBinaryAnimating] = useState(false)

  const orderStatus = getOrderStatus(outcome, question, parentQuestion)
  const positionLabels = getPositionLabels(outcome, question || parentQuestion)

  const handleLongClick = e => {
    e.stopPropagation()
    if (outcome?.status === 'active' && onClick) {
      onClick(outcome, constants.position.LONG)
    }
  }

  const handleShortClick = e => {
    e.stopPropagation()
    if (outcome?.status === 'active' && onClick) {
      onClick(outcome, constants.position.SHORT)
    }
  }

  const handleOrderBookPositionChange = newPosition => {
    setPosition(newPosition)
    // The yes/no order book tabs only switch the book side; they must not pop the bet sheet.
    const shouldTriggerPlaceBet = !(isMobileSize && inMarketPage && displayMode === 'binary')
    if (outcome?.status === 'active' && onClick && shouldTriggerPlaceBet) {
      onClick(outcome, newPosition)
    }
  }

  const outcomeOnClick = () => {
    // Toggle accordion if available (for all statuses, even when not active)
    if (isAccordion && onAccordionToggle) {
      onAccordionToggle()
    }

    // Only trigger onClick for betting if clickable
    if (clickable) {
      // Prevent resetting position when opening accordion on mobile
      if (displayMode === 'segment' && (!isAccordionOpen || !isAccordion)) {
        if (!isMobileSize || !isAccordion || isAccordionOpen) {
          onClick(outcome, constants.position.LONG)
        }
      }
    }
  }

  const renderOutcomeAsSegment = () => {
    return (
      <div
        onClick={outcomeOnClick}
        onKeyDown={e => {
          if (!(clickable || isAccordion) || isDisabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            outcomeOnClick?.(e)
          }
        }}
        role={clickable || isAccordion ? 'button' : undefined}
        tabIndex={(clickable || isAccordion) && !isDisabled ? 0 : -1}
        aria-disabled={isDisabled || undefined}
        id={isAccordion ? accordionTriggerId : undefined}
        aria-expanded={isAccordion ? isAccordionOpen : undefined}
        aria-controls={isAccordion && isAccordionOpen ? accordionPanelId : undefined}
        onMouseEnter={() => !isMobileSize && setIsHovered(isAccordionOpen ? false : true)}
        onMouseLeave={() => !isMobileSize && setIsHovered(false)}
        ref={ref}
        className={classNames(
          'outcome-item outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
          className,
          {
            'outcome-item--big': big,
            'outcome-item--selected': selected === true,
            'outcome-item--selected-other': selected === false,
            'outcome-item--disabled': isDisabled === true,
            'outcome-item--clickable': clickable || isAccordion,
            'outcome-item--prediction': isPrediction === true,
            'outcome-item--prediction-wrong': isPredictionWrong === true,
            'outcome-item--winner': isWinner === true,
            'outcome-item--canceled': isCanceled,
            'outcome-item--market-page': inMarketPage,
          },
          {
            '!h-full': outcomeLength === 2 && !inMarketPage,
          },
          {
            'bg-negative-1! border-2 border-negative-3!': isOrderBookCrossed,
          }
        )}>
        {inMarketPage && outcome && outcome.thumbnail && (
          <Thumbnail
            image={outcome.thumbnail}
            alt={outcome.title}
            className="rounded-[var(--radius-sm)] z-10 w-12 h-12"
            variant="rect"
          />
        )}
        {outcome && (
          <div className={classNames('flex items-center justify-between w-full z-10 gap-2')}>
            <div
              className={`outcome-item__title ${inMarketPage ? 'outcome-item__title--market-page' : ''} truncate leading-[100%] min-w-0 flex-1 ${isOrderBookCrossed ? 'text-negative-5' : ''}`}>
              {outcome.title}
              {isOrderBookCrossed && (
                <span className="ml-2 text-xs font-semibold text-negative-5">⚠️ Crossed Orderbook</span>
              )}
            </div>

            {!big &&
              outcome &&
              question &&
              question.status === QUESTION_STATUS.OPEN &&
              outcome?.status === 'active' &&
              (!isHovered ||
                (selected && isAccordionOpen) ||
                (question.outcomes.length <= 2 && !isCustomBinary && !shouldShowPositionButtons)) &&
              ((question && question.status === QUESTION_STATUS.OPEN) || (parentQuestion && parentQuestion.status === QUESTION_STATUS.OPEN)) &&
              outcome.price[marketCurrencyId] !== undefined &&
              !hideOutcomePrice && (
                <div className="flex z-10 flex-shrink-0">
                  <div
                    className={`font-bold w-[max-content] transition-colors duration-300 ${
                      colorClass !== '' ? colorClass : 'text-Neutral-6'
                    } ${inMarketPage ? 'text-xl' : 'text-base'}`}>
                    <DeferredNumberFlow value={100 * value} suffix="%" />
                  </div>
                </div>
              )}

            {orderStatus && (
              <div className="flex items-center gap-2 z-20 flex-shrink-0">
                <Tag type={orderStatus.type}>
                  <div className="flex items-center gap-1">
                    {orderStatus.icon && orderStatus.icon}
                    <span>{orderStatus.text}</span>
                  </div>
                </Tag>
              </div>
            )}

            {isHovered &&
              !isMobileSize &&
              shouldShowPositionButtons &&
              !isAccordionOpen &&
              outcome?.status === 'active' && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="relative rounded-sm contain-content">
                    <div className="absolute inset-0 z-10 w-full h-full flex items-center justify-center bg-surface-3" />
                    <button
                      className={`bg-positive-4/30 z-20 relative hover:bg-positive-4/50 text-Neutral-6 px-4 ${inMarketPage ? 'h-10' : 'h-8'} text-xs font-bold cursor-pointer`}
                      onClick={handleLongClick}
                      onMouseEnter={() => setYesButtonHovered(true)}
                      onMouseLeave={() => setYesButtonHovered(false)}>
                      <span className="relative">
                        {yesButtonHovered ? `${Math.round(100 * value)}%` : positionLabels.longLabel}
                      </span>
                    </button>
                  </div>
                  <div className="relative rounded-sm contain-content">
                    <div className="absolute inset-0 z-10 w-full h-full flex items-center justify-center bg-surface-3" />
                    <button
                      className={`bg-auxiliaries-3/30 z-20 relative hover:bg-auxiliaries-3/50 text-Neutral-6 px-4 ${inMarketPage ? 'h-10' : 'h-8'} text-xs font-bold cursor-pointer`}
                      onClick={handleShortClick}
                      onMouseEnter={() => setNoButtonHovered(true)}
                      onMouseLeave={() => setNoButtonHovered(false)}>
                      <span className="relative z-10">
                        {noButtonHovered ? `${Math.round(100 * (1 - value))}%` : positionLabels.shortLabel}
                      </span>
                    </button>
                  </div>
                </div>
              )}

            {isHovered &&
              !isMobileSize &&
              isCustomBinary &&
              !shouldShowPositionButtons &&
              !isAccordionOpen &&
              outcome?.status === 'active' && (
                <div className="flex items-center flex-shrink-0">
                  <Button
                    variant="primary"
                    type="solid"
                    size="large"
                    className="!h-8 !px-4 bg-brand-04"
                    buttonType="button"
                    onClick={handleLongClick}>
                    <span className="text-sm font-bold">{i18n('place_bet.bet')}</span>
                  </Button>
                </div>
              )}
          </div>
        )}

        {((question && question.status === QUESTION_STATUS.OPEN) || (parentQuestion && parentQuestion.status === QUESTION_STATUS.OPEN)) && (
          <div
            className={classNames('outcome-item__chart', { 'opacity-0 saturate-0': outcome?.status !== 'active' })}
            style={{ width: width + '%' }}></div>
        )}

        {isAccordion && (
          <div className="p-[10px] !z-[100]">
            <>
              {isAccordionOpen && (
                <ArrowCaretUpIcon
                  fill={isOrderBookCrossed ? '#dc2626' : 'var(--color-Neutral-6)'}
                  width={20}
                  height={20}
                />
              )}
              {!isAccordionOpen && (
                <ArrowCaretDownIcon
                  fill={isOrderBookCrossed ? '#dc2626' : 'var(--color-Neutral-6)'}
                  width={20}
                  height={20}
                />
              )}
            </>
          </div>
        )}
      </div>
    )
  }


  const renderOutcomeAsBinary = () => {
    const outcomePercentage = Math.round(100 * (outcome.price[marketCurrencyId] || 0))
    const oppositePercentage = Math.round(100 * (1 - (outcome.price[marketCurrencyId] || 0)))
    const marketUi = deriveMarketUi(question || parentQuestion)
    const showSingleMobileBetButton =
      isMobileSize &&
      inMarketPage &&
      !marketUi.binaryLabelStyle &&
      (question?.outcomes?.length === 2 || outcome._virtualPosition !== undefined)

    const handleBinaryClick = () => {
      if (isAccordion) {
        setIsBinaryAnimating(true)
        onAccordionToggle()
        setTimeout(() => {
          setIsBinaryAnimating(false)
        }, 500)
      } else if (clickable) {
        outcomeOnClick()
      }
    }

    return (
      <div
        className={`outcome-row outcome-row--binary flex flex-col gap-4 pb-4 transition-colors duration-300 rounded-lg ${
          isBinaryAnimating ? '' : 'bg-transparent'
        } ${isOrderBookCrossed ? 'bg-negative-1 border-2 border-negative-3' : ''}`}>
        <div
          className="outcome-row__main flex items-center gap-4 justify-between outline-offset-2 focus-visible:outline-[var(--color-brand-04)]"
          role={clickable || isAccordion ? 'button' : undefined}
          tabIndex={(clickable || isAccordion) && !isDisabled ? 0 : -1}
          aria-disabled={isDisabled || undefined}
          id={isAccordion ? accordionTriggerId : undefined}
          aria-expanded={isAccordion ? isAccordionOpen : undefined}
          aria-controls={isAccordion && isAccordionOpen ? accordionPanelId : undefined}
          onClick={handleBinaryClick}
          onKeyDown={e => {
            if (!(clickable || isAccordion) || isDisabled) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleBinaryClick()
            }
          }}>
          <div className="outcome-row__label flex gap-3 md:gap-4 items-center min-w-0 flex-1">
            {outcome.thumbnail && (
              <Thumbnail
                image={outcome.thumbnail}
                alt={outcome.title}
                className={classNames('z-10 w-12 h-12', {
                  'rounded-[var(--radius-sm)]': inMarketPage,
                  'rounded-xl': !inMarketPage,
                })}
                variant="rect"
              />
            )}
            <div
              className={`outcome-item__title ${inMarketPage ? 'outcome-item__title--market-page' : ''} truncate leading-[100%] min-w-0 ${isOrderBookCrossed ? 'text-negative-5' : ''}`}>
              {outcome.title}
              {isOrderBookCrossed && (
                <span className="ml-2 text-xs font-semibold text-negative-5">⚠️ Crossed Orderbook</span>
              )}
            </div>
          </div>

          <div className="outcome-row__percentage flex items-center gap-2 flex-shrink-0">
            {!big &&
              outcome &&
              outcome.status === 'active' &&
              (!isHovered || (question && question.is_binary)) &&
              ((question && question.status === QUESTION_STATUS.OPEN) || (parentQuestion && parentQuestion.status === QUESTION_STATUS.OPEN)) && (
                <div className="flex z-10">
                  {outcome.price[marketCurrencyId] !== undefined && !hideOutcomePrice && (
                    <div
                      className={`font-bold w-[max-content] transition-colors duration-300 ${
                        colorClass !== '' ? colorClass : 'text-Neutral-6'
                      } ${inMarketPage ? 'text-xl' : 'text-base'}`}>
                      <DeferredNumberFlow value={100 * value} suffix="%" />
                    </div>
                  )}
                </div>
              )}
            {orderStatus && (
              <Tag type={orderStatus.type}>
                <div className="flex items-center gap-1">
                  {orderStatus.icon && orderStatus.icon}
                  <span>{orderStatus.text}</span>
                </div>
              </Tag>
            )}
          </div>
        </div>

        {outcome?.status === 'active' && !isQuestionResolved && (
          <>
            {showSingleMobileBetButton ? (
              <CustomBinaryMarketBetButton
                hoverContent={`${outcomePercentage}%`}
                onClick={handleLongClick}
                isSelected={false}
              />
            ) : (
              <BinaryButtons
                firstButton={{
                  label: positionLabels.longLabel,
                  icon: null,
                  hoverContent: `${outcomePercentage}%`,
                  onClick: handleLongClick,
                  outcomeId: null,
                  className: 'bg-positive-4/30 text-Neutral-6 hover:bg-positive-4/50',
                }}
                secondButton={{
                  label: positionLabels.shortLabel,
                  icon: null,
                  hoverContent: `${oppositePercentage}%`,
                  onClick: handleShortClick,
                  outcomeId: null,
                  className: 'bg-auxiliaries-3/30 text-Neutral-6 hover:bg-auxiliaries-3/50',
                }}
                selectedId={null}
                size={inMarketPage && !isMobileSize ? 'large' : 'medium'}
              />
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className={classNames(outcomeLength === 2 && !inMarketPage ? 'h-2/4' : '', {
        'outcome-accordion-shell overflow-hidden rounded-[var(--radius-xs)] transition-colors duration-300 md:border md:border-solid md:focus-within:border-[var(--color-brand-04)]':
          isAccordion,
        'md:border-[var(--color-brand-04)]': isAccordion && isActiveMarket,
        'md:border-Neutral-3 md:hover:border-Neutral-6': isAccordion && !isActiveMarket,
      })}>
      {displayMode === 'segment' && renderOutcomeAsSegment()}
      {displayMode === 'binary' && renderOutcomeAsBinary()}

      <AnimatePresence initial={false}>
        {isAccordionOpen && isAccordion && (
          <motion.div
            key="outcome-accordion"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { type: 'spring', stiffness: 260, damping: 32, mass: 0.9 },
              opacity: { duration: 0.18, ease: 'easeOut' },
            }}
            id={accordionPanelId}
            role="region"
            aria-labelledby={accordionTriggerId}
            className="overflow-hidden">
            <OutcomeAccordion
              outcome={outcome}
              question={question}
              parentQuestion={parentQuestion}
              activeCurrency={activeCurrency}
              isOrderBookAvailable={isOrderBookAvailable}
              isMultiOutcomeMarket={isMultiOutcomeMarket}
              longOrderbook={longOrderbook}
              shortOrderbook={shortOrderbook}
              userOrders={userOrders}
              displayMode={displayMode}
              onPriceClick={onPriceClick}
              handleOrderBookPositionChange={handleOrderBookPositionChange}
              position={position}
              setPosition={setPosition}
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
              shouldShowPositionButtons={shouldShowPositionButtons}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const OutcomeItemWithRefs = forwardRef(OutcomeItem)
export default OutcomeItemWithRefs
